import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getUsersFromOrganizationForAgenturen,
  updateUserRole, 
  deleteUser, 
  updateUserData, 
  toggleUserStatus,
  getTeamsByAgentur
} from '../api';
import AddMemberForm from './CreateUser';
import EditUserDialog from '../components/common/EditUserDialog';
import DashboardNav from '../components/dashboard/DashboardNav';

function TeamMitarbeiter({ user }) {
  const { agenturId, teamId } = useParams();
  const navigate = useNavigate();
  
  const [members, setMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]); // Ungefilterte Mitglieder
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [agentur, setAgentur] = useState(null);
  const [currentTeam, setCurrentTeam] = useState(null);

  const [filters, setFilters] = useState({
    sortBy: 'full_name', // full_name, team_id, created_at
    sortOrder: 'asc', // asc, desc
    teamFilter: '', // Team-Filter
  });

  const isAdmin = user?.isAdmin || false;
  const isOrgLeader = user?.isOrgLeader || false;
  const isAgenturLeader = user?.isAgenturLeader || false;
  const isTeamlead = user?.isTeamlead || false;

  // Filter-Handler
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      sortBy: 'full_name',
      sortOrder: 'asc',
      teamFilter: '',
    });
  };

  // Mitglieder laden - je nachdem ob agenturId oder teamId vorhanden ist
  const loadAllMembers = async () => {
    setLoading(true);
    setError('');
    
    try {
      let response;
      if (agenturId) {
        // Immer alle Agentur-Daten laden
        response = await getUsersFromOrganizationForAgenturen(user.organization_id);
        if (response.success && Array.isArray(response.agenturen)) {
          // Agentur mit passender ID finden
          const found = response.agenturen.find(a => a.agentur.id.toString() === agenturId.toString());
          if (found) {
            let mitglieder = found.mitglieder || [];
            // Wenn teamId gesetzt ist, filtere die Mitglieder nach Team
            if (teamId) {
              mitglieder = mitglieder.filter(m => m.a_team_id && m.a_team_id.toString() === teamId.toString());
              // Team-Informationen laden
              const teamsResponse = await getTeamsByAgentur(user.id, agenturId);
              if (teamsResponse.success) {
                const foundTeam = teamsResponse.teams.find(t => t.id.toString() === teamId.toString());
                setCurrentTeam(foundTeam);
              }
            }
            setAllMembers(mitglieder);
          } else {
            setError('Agentur nicht gefunden.');
            setAllMembers([]);
          }
        } else {
          setError(response.error || 'Fehler beim Laden der Mitglieder');
          setAllMembers([]);
        }
      } else {
        setError('Keine Agentur-ID angegeben');
        setAllMembers([]);
        setLoading(false);
        return;
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
      setAllMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter anwenden (separate Funktion)
  const applyFilters = () => {
    let filteredMembers = [...allMembers];
    
    // Team-Filter anwenden (nur wenn wir in Agentur-Ansicht sind)
    if (filters.teamFilter && !teamId) {
      filteredMembers = filteredMembers.filter(member => 
        member.a_team_id && member.a_team_id.toString() === filters.teamFilter.toString()
      );
    }
    
    // Sortierung anwenden
    filteredMembers.sort((a, b) => {
      let compareValue = 0;
      
      switch (filters.sortBy) {
        case 'full_name':
          compareValue = (a.full_name || '').localeCompare(b.full_name || '');
          break;
        case 'team_id':
          const teamA = getTeamName(a.a_team_id);
          const teamB = getTeamName(b.a_team_id);
          compareValue = teamA.localeCompare(teamB);
          break;
        case 'created_at':
          compareValue = new Date(a.created_at || 0) - new Date(b.created_at || 0);
          break;
        default:
          compareValue = (a.full_name || '').localeCompare(b.full_name || '');
      }
      
      return filters.sortOrder === 'desc' ? -compareValue : compareValue;
    });
    
    setMembers(filteredMembers);
  };
  
  // Teams laden (nur für Agentur-Ansicht)
  const loadTeams = async () => {
    if (!agenturId || teamId) return; // Nur laden wenn wir in Agentur-Ansicht sind
    
    try {
      const response = await getTeamsByAgentur(user.id, agenturId);
      
      if (response.success) {
        setTeams(response.teams);
      } else {
        setError(response.error || 'Fehler beim Laden der Teams');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };
  
  // Beim ersten Laden Daten abrufen
  useEffect(() => {
    if (agenturId || teamId) {
      loadAllMembers();
      if (!teamId) {
        loadTeams(); // Nur Teams laden wenn wir in Agentur-Ansicht sind
      }
    }
  }, [agenturId, teamId]);

  // Filter anwenden wenn sich allMembers oder filters ändern
  useEffect(() => {
    if (allMembers.length > 0) {
      applyFilters();
    }
  }, [allMembers, filters]);
  
  // Rollenname basierend auf role_type
  const getRoleName = (roleType) => {
    switch (parseInt(roleType, 10)) {
      case 1: return 'Administrator';
      case 2: return 'Teamleiter';
      case 3: return 'Organisationsleiter';
      case 4: return 'Callcenter Leader';
      case 5: return 'Callcenter Mitarbeiter';
      case 6: return 'Fachberater';
      case 7: return 'Agentur Leiter';
      default: return 'Benutzer';
    }
  };
  
  // Team-Name basierend auf team_id
  const getTeamName = (teamId) => {
    if (!teamId) return 'Kein Team';
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unbekanntes Team';
  };

  // Handler für Navigation
  const handleLogout = () => {
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'agenturen') {
      navigate('/agenturen');
    } else if (section === 'agentur-dashboard') {
      navigate(`/agentur-dashboard/${agenturId}`);
    } else if (section === 'mitglieder') {
      // Bleibe auf der aktuellen Seite
    } else if (section === 'leads') {
      if (teamId) {
        navigate(`/agenturen/${agenturId}/teams/${teamId}/leads`);
      } else {
        navigate(`/agenturen/${agenturId}/leads`);
      }
    }
  };

  // Zurück zur Agentur-Teams-Ansicht
  const handleBackToTeams = () => {
    if (teamId) {
      navigate(`/agenturen/${agenturId}/teams`);
    } else {
      navigate(`/agenturen/${agenturId}`);
    }
  };

  // Benutzer bearbeiten
  const handleEditUser = (member) => {
    setSelectedUserForEdit(member);
    setShowEditUserDialog(true);
  };

  const handleEditUserSuccess = () => {
    setShowEditUserDialog(false);
    setSelectedUserForEdit(null);
    loadAllMembers(); // Daten neu laden
  };

  const handleEditUserCancel = () => {
    setShowEditUserDialog(false);
    setSelectedUserForEdit(null);
  };

  // Benutzer löschen
  const handleDeleteUser = async (memberId) => {
    if (!window.confirm('Möchten Sie diesen Benutzer wirklich löschen?')) {
      return;
    }

    try {
      const response = await deleteUser(user.id, memberId);
      if (response.success) {
        loadAllMembers(); // Daten neu laden
      } else {
        setError(response.error || 'Fehler beim Löschen des Benutzers');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  // Benutzer-Status ändern
  const handleToggleUserStatus = async (member) => {
    const newStatus = member.active === 1 ? 0 : 1;
    const statusText = newStatus === 1 ? 'aktivieren' : 'deaktivieren';
    
    if (!window.confirm(`Möchten Sie ${member.full_name} wirklich ${statusText}?`)) {
      return;
    }

    try {
      const response = await toggleUserStatus(user.id, member.id, newStatus);
      if (response.success) {
        loadAllMembers(); // Daten neu laden
      } else {
        setError(response.error || `Fehler beim ${statusText} des Benutzers`);
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  // Neues Mitglied hinzufügen - wird automatisch durch onClose in CreateUser aufgerufen
  const handleSaveMember = () => {
    setShowAddMemberForm(false);
    loadAllMembers(); // Daten neu laden
  };

  // Wenn keine Berechtigung, zeige Meldung
  if (!isAdmin && !isOrgLeader && !isAgenturLeader && !isTeamlead) {
    return (
      <div className="team-mitarbeiter-container">
        <div className="page-header">
          <button className="back-button" onClick={handleBackToTeams}>
            ← Zurück
          </button>
          <h2>Mitarbeiter verwalten</h2>
        </div>
        <div className="no-permission">
          <p>Sie haben keine Berechtigung, Mitarbeiter zu verwalten.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardNav
        activeSection="mitglieder"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => {}}
        onOpenMembersManagement={() => {}}
        onViewOrganizationLeads={() => {}}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      
      <div className="team-mitarbeiter-container">
        <div className="page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            ← Zurück
          </button>
          <h2>
            {teamId ? 
              `Mitarbeiter - ${currentTeam?.name || 'Team'}` : 
              `Mitarbeiter - Agentur ${agenturId}`
            }
          </h2>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        {loading && allMembers.length === 0 ? (
          <div className="loading">Mitarbeiter werden geladen...</div>
        ) : (
          <>
            <div className="members-actions">
            </div>
            
            {/* Filter nur anzeigen wenn wir in Agentur-Ansicht sind */}
            {!teamId && (
              <div className="leads-filters">
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Sortieren nach:</label>
                    <select 
                      value={filters.sortBy} 
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    >
                      <option value="full_name">Name</option>
                      <option value="team_id">Team</option>
                      <option value="created_at">Erstellungsdatum</option>
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>Reihenfolge:</label>
                    <select 
                      value={filters.sortOrder} 
                      onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                    >
                      <option value="asc">Aufsteigend</option>
                      <option value="desc">Absteigend</option>
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>Projekt:</label>
                    <select 
                      value={filters.teamFilter}
                      onChange={(e) => handleFilterChange('teamFilter', e.target.value)}
                    >
                      <option value="">Alle Teams</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <button className="reset-filters-button" onClick={resetFilters}>
                      Filter zurücksetzen
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="members-table-container">
              <table className="members-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>E-Mail</th>
                    <th>Rolle</th>
                    {!teamId && <th>Projekt</th>}
                    <th>Status</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {members.length === 0 ? (
                    <tr>
                      <td colSpan={teamId ? 5 : 6} className="no-data">
                        {filters.teamFilter ? 
                          'Keine Mitarbeiter mit den aktuellen Filtern gefunden' : 
                          'Keine Mitarbeiter gefunden'
                        }
                      </td>
                    </tr>
                  ) : (
                    members.map(member => (
                      <tr key={member.id}>
                        <td>{member.full_name || 'Kein Name'}</td>
                        <td>{member.email}</td>
                        <td>{getRoleName(member.role_type)}</td>
                        {!teamId && <td>{getTeamName(member.a_team_id)}</td>}
                        <td>
                          <span className={`status-badge ${member.active === 1 ? 'active' : 'inactive'}`}>
                            {member.active === 1 ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td>
                          <div className="member-actions">
                            {/* Bearbeiten-Button für Admin/OrgLeiter/AgenturLeiter */}
                            {(isAdmin || isOrgLeader || isAgenturLeader) && (
                              <button
                                className="edit-user-button"
                                onClick={() => handleEditUser(member)}
                                title="Benutzer bearbeiten"
                              >
                                Bearbeiten
                              </button>
                            )}
                            {/* Status-Button für Admin/OrgLeiter/AgenturLeiter/Teamleiter */}
                            {(isAdmin || isOrgLeader || isAgenturLeader || isTeamlead) && member.id !== user.id && (
                              <button
                                className={`toggle-status-button ${member.active === 1 ? 'deactivate' : 'activate'}`}
                                onClick={() => handleToggleUserStatus(member)}
                                title={member.active === 1 ? 'Benutzer deaktivieren' : 'Benutzer aktivieren'}
                              >
                                {member.active === 1 ? 'Deaktivieren' : 'Aktivieren'}
                              </button>
                            )}
                            {/* Löschen nur für Admin oder OrgLeiter */}
                            {(isAdmin || isOrgLeader) && member.id !== user.id && (
                              <button 
                                className="delete-member-button" 
                                onClick={() => handleDeleteUser(member.id)}
                              >
                                Löschen
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
        
        {/* Formular für neues Mitglied */}
        {showAddMemberForm && (
          <div className="modal-overlay">
            <div className="modal-dialog large">
              <div className="modal-header">
                <h3>Neuen Benutzer erstellen</h3>
                <button className="close-button" onClick={() => setShowAddMemberForm(false)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="modal-content">
                <AddMemberForm
                  user={user}
                  onClose={() => setShowAddMemberForm(false)}
                  preselectedAgenturId={agenturId}
                  preselectedTeamId={teamId}
                />
              </div>
            </div>
          </div>
        )}

        {/* EditUserDialog Modal */}
        {showEditUserDialog && selectedUserForEdit && (
          <EditUserDialog
            user={user}
            targetUser={selectedUserForEdit}
            onSave={handleEditUserSuccess}
            onCancel={handleEditUserCancel}
            isMobile={false}
            isTablet={false}
          />
        )}

        {/* Inline Styles */}
        <style>{`
          .team-mitarbeiter-container {
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
          }
          
          .page-header {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 24px;
          }
          
          .back-button {
            padding: 8px 16px;
            background: #f7fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            color: #4a5568;
            transition: all 0.2s;
          }
          
          .back-button:hover {
            background: #edf2f7;
            border-color: #cbd5e0;
          }
          
          .error-message {
            background: #fed7d7;
            color: #c53030;
            padding: 12px 16px;
            border-radius: 6px;
            margin-bottom: 16px;
            border: 1px solid #feb2b2;
          }
          
          .loading {
            text-align: center;
            padding: 40px;
            color: #718096;
            font-size: 16px;
          }
          
          .members-actions {
            margin-bottom: 20px;
          }
          
          .add-member-button {
            padding: 10px 20px;
            background: #3182ce;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: background-color 0.2s;
          }
          
          .add-member-button:hover {
            background: #2c5aa0;
          }
          
          .leads-filters {
            background: white;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 20px;
          }
          
          .filter-row {
            display: flex;
            gap: 16px;
            align-items: center;
            flex-wrap: wrap;
          }
          
          .filter-group {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          
          .filter-group label {
            font-size: 12px;
            font-weight: 500;
            color: #4a5568;
          }
          
          .filter-group select {
            padding: 6px 12px;
            border: 1px solid #d1d5db;
            border-radius: 4px;
            font-size: 14px;
            min-width: 120px;
          }
          
          .reset-filters-button {
            padding: 6px 12px;
            background: #f7fafc;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            color: #4a5568;
            margin-top: 16px;
          }
          
          .reset-filters-button:hover {
            background: #edf2f7;
          }
          
          .members-table-container {
            overflow-x: auto;
            background: white;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
          }
          
          .members-table {
            width: 100%;
            border-collapse: collapse;
            min-width: 800px;
          }
          
          .members-table th {
            background: #f7fafc;
            padding: 12px 16px;
            text-align: left;
            font-weight: 600;
            color: #2d3748;
            border-bottom: 1px solid #e2e8f0;
            font-size: 14px;
          }
          
          .members-table td {
            padding: 12px 16px;
            border-bottom: 1px solid #f1f5f9;
            font-size: 14px;
            color: #4a5568;
          }
          
          .members-table tr:hover {
            background: #f7fafc;
          }
          
          .no-data {
            text-align: center;
            color: #a0aec0;
            font-style: italic;
          }
          
          .status-badge {
            display: inline-block;
            padding: 0.2rem 0.5rem;
            border-radius: 0.25rem;
            font-size: 0.65rem;
            font-weight: 500;
            text-align: center;
            white-space: nowrap;
            min-width: 50px;
            max-width: 90px;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          .status-badge.active {
            background-color: #c6f6d5;
            color: #2f855a;
          }
          
          .status-badge.inactive {
            background-color: #fed7d7;
            color: #c53030;
          }
          
          .member-actions {
            display: flex;
            gap: 4px;
            flex-wrap: wrap;
          }
          
          .edit-user-button {
            padding: 4px 8px;
            font-size: 0.7rem;
            margin: 1px;
            border: none;
            border-radius: 3px;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.2s;
            background: #3182ce;
            color: white;
          }
          
          .edit-user-button:hover {
            background: #2c5aa0;
          }
          
          .toggle-status-button {
            padding: 4px 8px;
            font-size: 0.7rem;
            margin: 1px;
            border: none;
            border-radius: 3px;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.2s;
          }
          
          .toggle-status-button.activate {
            background: #38a169;
            color: white;
          }
          
          .toggle-status-button.activate:hover {
            background: #2f855a;
          }
          
          .toggle-status-button.deactivate {
            background: #d69e2e;
            color: white;
          }
          
          .toggle-status-button.deactivate:hover {
            background: #b7791f;
          }
          
          .delete-member-button {
            padding: 4px 8px;
            font-size: 0.7rem;
            margin: 1px;
            border: none;
            border-radius: 3px;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.2s;
            background: #e53e3e;
            color: white;
          }
          
          .delete-member-button:hover {
            background: #c53030;
          }
          
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }
          
          .modal-dialog {
            background: white;
            border-radius: 8px;
            padding: 24px;
            max-width: 500px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
          }
          
          .no-permission {
            text-align: center;
            padding: 40px;
            color: #718096;
            background: white;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
          }
        `}</style>
      </div>
    </>
  );
}

export default TeamMitarbeiter; 